/**
 * Sliding-window bytes/sec meter for `HttpEventType.UploadProgress`.
 *
 * A window rather than adjacent-event deltas because XHR emits progress
 * events milliseconds apart: `dt` is frequently ~0 (→ Infinity) and always
 * noisy. The figure is still mildly optimistic by nature — `loaded` counts
 * bytes handed to the socket send buffer, not bytes the server acknowledged
 * — so callers should stop sampling once `loaded === total` rather than
 * publish the buffer-drain rate as the upload rate (see call sites).
 */
export class UploadSpeedMeter {
  private readonly samples: { t: number; loaded: number }[] = [];
  // -Infinity, not 0: `now - lastEmitAt < emitEveryMs` must never throttle an
  // upload's first emit just because it happens to land within
  // `emitEveryMs` of `reset()`'s timestamp.
  private lastEmitAt = -Infinity;

  constructor(
    private readonly windowMs = 3000,
    private readonly emitEveryMs = 500,
    private readonly minSpanMs = 250
  ) {}

  /** Resets the meter — call on `HttpEventType.Sent`, before any progress event. */
  reset(now: number): void {
    this.samples.length = 0;
    this.samples.push({ t: now, loaded: 0 });
    this.lastEmitAt = -Infinity;
  }

  /**
   * Feeds one progress event. Returns bytes/sec, or `undefined` while the
   * window is still too short or the emit throttle has not elapsed — in
   * which case the caller leaves the previously reported speed untouched.
   */
  sample(loaded: number, now: number): number | undefined {
    this.samples.push({ t: now, loaded });

    // Keep one anchor older than the window so the measured span stays ~windowMs
    // instead of collapsing back to the newest pair.
    while (this.samples.length > 2 && now - this.samples[1].t > this.windowMs) {
      this.samples.shift();
    }

    const first = this.samples[0];
    const span = now - first.t;
    // span <= 0 regardless of minSpanMs: two same-millisecond events are a
    // real XHR occurrence, and dividing by a zero/negative span is never
    // meaningful, however small minSpanMs is configured.
    if (span <= 0 || span < this.minSpanMs) return undefined;
    if (now - this.lastEmitAt < this.emitEveryMs) return undefined;

    this.lastEmitAt = now;
    return ((loaded - first.loaded) * 1000) / span;
  }
}
